import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  TextField,
  Paper,
  Skeleton,
  Alert,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  Groups as MeetingIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
  EventNote as EventNoteIcon,
} from '@mui/icons-material';
import { Event } from 'api/canprospect/event';
import getProspectHistoryEvents from 'api/canprospect/getProspectHistoryEvents';
import getProspectHistoryEventTypes from 'api/canprospect/getProspectHistoryEventTypes';
import updateEvent from 'api/canprospect/updateEvent';
import deleteProspectHistoryEvent from 'api/canprospect/deleteProspectHistoryEvent';
import AddEventModal from '../Events/AddEventModal';

const STATUS_COLORS: Record<string, string> = {
  'New Prospect':      '#d7f7fc',
  'Contacted':         '#d7fce4',
  'Replied':           '#d7fce4',
  'Warm Lead':         '#f3fcd7',
  'Qualified Lead':    '#fcf6d7',
  'Proposal Stage':    '#fce6d7',
  'Negotiation Stage': '#fcd7d7',
  'Closed Won':        '#fcd7f1',
  'Closed Lost':       '#e6d7fc',
};

const EVENT_TYPE_ICONS: Record<string, React.ReactElement> = {
  Call:    <PhoneIcon fontSize="small" />,
  Email:   <EmailIcon fontSize="small" />,
  Meeting: <MeetingIcon fontSize="small" />,
};

const EVENT_TYPE_BORDER_COLORS: Record<string, string> = {
  Call:    '#1976d2',
  Email:   '#2e7d32',
  Meeting: '#ed6c02',
};

interface ProspectHistoryEventType {
  Id: number;
  Type: string;
}

interface ProspectHistoryTabProps {
  prospectId: number | null;
  prospectStatusId: number | null;
}

interface EventCardProps {
  event: Event;
  eventTypes: ProspectHistoryEventType[];
  onNotesUpdated: () => void;
  onDeleted: (id: number) => void;
}

function EventCard({ event, eventTypes, onNotesUpdated, onDeleted }: EventCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [editNotes, setEditNotes] = useState(event.comment);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const borderColor = EVENT_TYPE_BORDER_COLORS[event.eventType] || '#9e9e9e';
  const icon = EVENT_TYPE_ICONS[event.eventType] || <EventNoteIcon fontSize="small" />;
  const statusColor = STATUS_COLORS[event.prospect] || '#e0e0e0';

  const handleSaveNotes = async () => {
    setIsSaving(true);
    try {
      await updateEvent(event.id, editNotes);
      onNotesUpdated();
      setExpanded(false);
    } catch (err) {
      console.error('Error updating notes:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteProspectHistoryEvent(event.id);
      onDeleted(event.id);
      setDeleteOpen(false);
    } catch (err) {
      console.error('Error deleting event:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderLeft: 4,
        borderLeftColor: borderColor,
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'box-shadow 0.2s ease',
        '&:hover': { boxShadow: 2 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1.5,
          cursor: 'pointer',
          gap: 1.5,
        }}
        onClick={() => setExpanded(!expanded)}
        role="button"
        aria-expanded={expanded}
        aria-label={`${event.eventType} event on ${event.date}`}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            borderRadius: '50%',
            bgcolor: `${borderColor}14`,
            color: borderColor,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {event.eventType} Event
            </Typography>
            <Chip
              label={event.prospect}
              size="small"
              sx={{
                bgcolor: statusColor,
                fontWeight: 600,
                fontSize: '0.65rem',
                height: 20,
              }}
            />
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {event.date} {event.time && `at ${event.time}`}
            {event.consultant && event.consultant !== 'N/A' && ` \u2022 ${event.consultant}`}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mt: 0.5,
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {event.comment}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <IconButton
            size="small"
            color="error"
            onClick={(e) => { e.stopPropagation(); setDeleteOpen(true); }}
            aria-label="Delete event"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2, pt: 0.5, borderTop: 1, borderColor: 'divider' }}>
          <TextField
            label="Notes"
            multiline
            fullWidth
            rows={4}
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
            sx={{ mt: 1 }}
          />
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleSaveNotes}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => { setEditNotes(event.comment); setExpanded(false); }}
              disabled={isSaving}
            >
              Cancel
            </Button>
          </Box>
          {isSaving && <LinearProgress sx={{ mt: 1 }} />}
        </Box>
      </Collapse>

      {/* Delete Confirmation */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete Event</DialogTitle>
        <DialogContent>
          Are you sure you would like to delete this event?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="outlined"
            color="error"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

function LoadingSkeleton() {
  return (
    <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {[0, 1, 2].map((i) => (
        <Paper
          key={i}
          elevation={0}
          sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}
        >
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <Skeleton variant="circular" width={36} height={36} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="30%" height={22} />
              <Skeleton variant="text" width="50%" height={16} />
              <Skeleton variant="text" width="80%" height={16} />
            </Box>
          </Box>
        </Paper>
      ))}
    </Box>
  );
}

const ProspectHistoryTab: React.FC<ProspectHistoryTabProps> = ({
  prospectId,
  prospectStatusId,
}) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [eventTypes, setEventTypes] = useState<ProspectHistoryEventType[]>([]);

  const fetchEvents = useCallback(async () => {
    if (prospectId === null) {
      setError('Invalid prospect ID.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const historyEvents = await getProspectHistoryEvents(prospectId);
      const mapped: Event[] = historyEvents.map((event: any) => {
        const dateTime = event.Date?.trim() || '';
        const lastSpace = dateTime.lastIndexOf(' ');
        const date = lastSpace > 0 ? dateTime.substring(0, lastSpace).trim() : dateTime;
        const time = lastSpace > 0 ? dateTime.substring(lastSpace + 1).trim() : '';

        return {
          id: event.Id,
          prospect: event.ProspectStatus,
          consultant: event.Consultants?.map((c: any) => c.Name).join(', ') || 'N/A',
          date,
          time,
          comment: event.Notes || 'No comment provided.',
          eventType: event.EventType || 'Unknown',
        };
      });

      mapped.sort((a, b) => b.id - a.id);
      setEvents(mapped);
      setError(null);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [prospectId]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    const loadEventTypes = async () => {
      try {
        const { eventTypes: fetched } = await getProspectHistoryEventTypes();
        setEventTypes(fetched);
      } catch (err) {
        console.error('Error fetching event types:', err);
      }
    };
    loadEventTypes();
  }, []);

  const handleAddModalClose = () => {
    setAddModalOpen(false);
    fetchEvents();
  };

  const handleEventDeleted = (id: number) => {
    setEvents((prev) => prev.filter((e) => e.id !== id));
  };

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return (
      <Box sx={{ p: 2.5 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddModalOpen(true)}
          aria-label="Add new event"
          sx={{
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 2,
          }}
        >
          Add Event
        </Button>
      </Box>

      {events.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
          }}
        >
          <EventNoteIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            No events recorded for this prospect.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setAddModalOpen(true)}
            sx={{ mt: 2, textTransform: 'none' }}
          >
            Create First Event
          </Button>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {events.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              eventTypes={eventTypes}
              onNotesUpdated={fetchEvents}
              onDeleted={handleEventDeleted}
            />
          ))}
        </Box>
      )}

      <AddEventModal
        open={addModalOpen}
        onClose={handleAddModalClose}
        prospectId={prospectId}
        prospectStatusId={prospectStatusId}
      />
    </Box>
  );
};

export default ProspectHistoryTab;
